import useSWR from "swr";

import { Loader } from "components/customElements/loader";
import { Title } from "components/customElements/title";
import ErrorBoundary from "components/ErrorBoundary";
import RichContent from "components/RichContent";
import useAuthentication from "hooks/useAuthentication";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

type InsightDetailProps = {
  slug: string;
};

export const insightDetailUrl = (slug: string) => {
  const insightUrl = new ApiEndpoint(`/api/v2/insights/${slug}`);
  insightUrl.searchParams.append(
    "fields[insights]",
    "detail,time-to-read,is-public,from-search-bot,account,company"
  );

  insightUrl.searchParams.append(
    "include",
    ["account", "company", "account.premium-service-catalogues"].join(",")
  );

  insightUrl.searchParams.append("no-view-count", "true");

  return insightUrl.href;
};

const InsightDetail = ({ slug }: InsightDetailProps) => {
  const { authenticated, account: loggedInAccount } = useAuthentication();
  const { data: insight } = useSWR(insightDetailUrl(slug), baseFetcher);

  if (!insight) {
    return (
      <div>
        <Title>{authenticated ? "Detail" : "Full Insight"}</Title>
        <Loader type="paragraph" numberOfItems={5} />
      </div>
    );
  }

  if (!insight?.detail) return <></>;

  const detail = insight?.detail?.replace(
    /<img ([^>]*)\/?>/g,
    '<img alt="" loading="lazy" $1 />'
  );

  const { timeToRead, isPublic } = insight;

  return (
    <ErrorBoundary>
      <div>
        <Title>
          {authenticated ? "Detail" : "Full Insight"}
          {!isPublic &&
            (!authenticated || loggedInAccount?.isPreviewPass) &&
            timeToRead && (
              <span className="text-ink-pink lowercase ml-[5px]">
                ({timeToRead}-minute read)
              </span>
            )}
        </Title>
        <RichContent
          html={detail}
          className="md:max-w-[745px]"
          insight={insight}
        />
      </div>
    </ErrorBoundary>
  );
};

export default InsightDetail;
